import { db } from '../config/firebase';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';

// Types for Still Active CSTs dashboard
export interface StillActiveCstData {
  term: string;
  asin: string;
  clicks: number;
  sales: number;
  stillActive: string;
  type?: string;
  probability?: number;
  killedInClicks?: number;
}

export interface ClickThresholds {
  cstClickThreshold: number;
  ptaClickThreshold: number;
}

export interface StillActiveCstsSummary {
  totalActiveCsts: number;
  clickThresholds: ClickThresholds;
  activeTerms: StillActiveCstData[];
}

// Type guards for Firestore data
interface PMT2Data {
  'Final Farming CST Max Clicks'?: number;
  'Final Farming PTA Max Clicks'?: number;
}

interface DispositionLedgerData {
  term?: string;
  percentProbability?: number;
  asin?: string;
  cst?: string;
  companyName?: string;
}

interface AllCstResultsData {
  term?: string;
  asin?: string;
  clicks?: number | string;
  sales?: number | string;
  stillActive?: string;
  type?: string;
  companyName?: string;
}

const isPMT2Data = (data: unknown): data is PMT2Data => {
  return typeof data === 'object' && data !== null;
};

const isDispositionLedgerData = (data: unknown): data is DispositionLedgerData => {
  return typeof data === 'object' && data !== null;
};

const isAllCstResultsData = (data: unknown): data is AllCstResultsData => {
  return typeof data === 'object' && data !== null;
};

/**
 * Fetches click thresholds from PMT2 data collection
 */
export const fetchClickThresholds = async (companyName: string): Promise<ClickThresholds> => {
  try {
    console.log(`Fetching click thresholds for company: ${companyName}`);
    
    // Try to fetch from pmt2_data collection
    const pmt2DataRef = doc(db, 'pmt2_data', companyName);
    const pmt2DataSnap = await getDoc(pmt2DataRef);
    
      if (pmt2DataSnap.exists()) {
        const data = pmt2DataSnap.data();
        if (isPMT2Data(data)) {
          return {
            cstClickThreshold: data['Final Farming CST Max Clicks'] || 25,
            ptaClickThreshold: data['Final Farming PTA Max Clicks'] || 15
          };
        }
      }
    
    console.warn(`No PMT2 data found for ${companyName}, using defaults`);
    return {
      cstClickThreshold: 25,
      ptaClickThreshold: 15
    };
  } catch (error) {
    console.error('Error fetching click thresholds:', error);
    return {
      cstClickThreshold: 25,
      ptaClickThreshold: 15
    };
  }
};

/**
 * Fetches probability data from disposition ledger for a specific ASIN
 */
export const fetchDispositionProbabilities = async (companyName: string, asin: string): Promise<Map<string, number>> => {
  try {
    console.log(`Fetching disposition probabilities for company: ${companyName}, ASIN: ${asin}`);
    
    const probabilityMap = new Map<string, number>();
    
    // Query the specific ASIN collection for this company
    const asinCollectionRef = collection(db, 'pmt3_disposition_ledger', companyName, asin);
    const termsSnapshot = await getDocs(asinCollectionRef);
    
    if (termsSnapshot.empty) {
      console.warn(`No disposition data found for ${companyName}/${asin}`);
      return probabilityMap;
    }
    
    termsSnapshot.forEach(termDoc => {
      const data = termDoc.data();
      if (isDispositionLedgerData(data) && data.term && typeof data.percentProbability === 'number') {
        probabilityMap.set(data.term.toLowerCase(), data.percentProbability);
      }
    });
    
    console.log(`Found ${probabilityMap.size} probability entries for ${companyName}/${asin}`);
    return probabilityMap;
  } catch (error) {
    console.error('Error fetching disposition probabilities:', error);
    return new Map<string, number>();
  }
};

/**
 * Calculates "Killed in X Clicks" for a term
 */
export const calculateKilledInClicks = (
  term: string,
  clicks: number,
  probability: number | undefined,
  thresholds: ClickThresholds
): number => {
  // If term starts with "B0", it's a PTA - subtract clicks from PTA threshold
  if (term.startsWith('B0')) {
    return thresholds.ptaClickThreshold - clicks;
  }
  
  // Otherwise it's a CST - multiply probability by CST threshold, then subtract clicks
  if (probability !== undefined) {
    const maxClicks = probability * thresholds.cstClickThreshold;
    return Math.round(maxClicks - clicks);
  }
  
  // If no probability data, use CST threshold directly
  return thresholds.cstClickThreshold - clicks;
};

/**
 * Fetches Still Active CST data for a specific company and ASIN
 */
export const fetchStillActiveCsts = async (companyName: string, asin: string): Promise<StillActiveCstsSummary> => {
  try {
    console.log(`Fetching Still Active CSTs for company: ${companyName}, ASIN: ${asin}`);
    
    // Fetch click thresholds and probabilities in parallel
    const [thresholds, probabilityMap] = await Promise.all([
      fetchClickThresholds(companyName),
      fetchDispositionProbabilities(companyName, asin)
    ]);
    
    const activeTerms: StillActiveCstData[] = [];
    let totalActiveCsts = 0;
    
    try {
      // Query the specific ASIN collection for this company
      const asinCollectionRef = collection(db, 'pmt3_all_cst_results', companyName, asin);
      const termsSnapshot = await getDocs(asinCollectionRef);
      
      if (termsSnapshot.empty) {
        console.warn(`No All CST Results data found for ${companyName}/${asin}`);
        return {
          totalActiveCsts: 0,
          clickThresholds: thresholds,
          activeTerms: []
        };
      }
      
      termsSnapshot.forEach(termDoc => {
        const data = termDoc.data();
        
        if (isAllCstResultsData(data)) {
          // Filter: stillActive = "Yes" and sales = 0
          const salesValue = typeof data.sales === 'number' ? data.sales : parseInt(String(data.sales)) || 0;
          
          if (data.stillActive === 'Yes' && salesValue === 0) {
            totalActiveCsts++;
            
            const term = data.term || termDoc.id;
            const clicksValue = typeof data.clicks === 'number' ? data.clicks : parseInt(String(data.clicks)) || 0;
            const probability = probabilityMap.get(term.toLowerCase());
            
            const termData: StillActiveCstData = {
              term,
              asin: asin, // Use the ASIN parameter
              clicks: clicksValue,
              sales: salesValue,
              stillActive: data.stillActive,
              type: data.type,
              probability: probability ? probability * 100 : undefined, // Convert to percentage
              killedInClicks: calculateKilledInClicks(term, clicksValue, probability, thresholds)
            };
            
            activeTerms.push(termData);
          }
        }
      });
      
      // Sort by clicks descending
      activeTerms.sort((a, b) => b.clicks - a.clicks);
      
      console.log(`Found ${totalActiveCsts} still active CSTs for ${companyName}/${asin}`);
      
      return {
        totalActiveCsts,
        clickThresholds: thresholds,
        activeTerms
      };
      
      // Sort by clicks descending
      activeTerms.sort((a, b) => b.clicks - a.clicks);
      
      console.log(`Found ${totalActiveCsts} still active CSTs for ${companyName}`);
      
      return {
        totalActiveCsts,
        clickThresholds: thresholds,
        activeTerms
      };
      
    } catch (queryError) {
      console.error('Error querying Still Active CSTs:', queryError);
      return {
        totalActiveCsts: 0,
        clickThresholds: thresholds,
        activeTerms: []
      };
    }
    
  } catch (error) {
    console.error('Error fetching Still Active CSTs:', error);
    throw error;
  }
};

/**
 * Gets available companies from PMT3 All CST Results collection
 */
export const getAvailableCompanies = async (): Promise<string[]> => {
  try {
    const collectionRef = collection(db, 'pmt3_all_cst_results');
    const querySnapshot = await getDocs(collectionRef);
    
    const companies: string[] = [];
    querySnapshot.forEach(doc => {
      companies.push(doc.id);
    });
    
    return companies.sort();
  } catch (error) {
    console.error('Error fetching available companies:', error);
    return [];
  }
};